import React, { useCallback, useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { Wand2, Save, CheckCheck, AlertTriangle, BookTemplate, Layers } from "lucide-react";
import { api, useFetch } from "@/lib/api";
import { useApp } from "@/context/AppContext";
import { PageHeader, Panel, StatusChip, LoadingState, EmptyState } from "@/components/app/primitives";
import { LiveBadge, SourceTag, ReviewStateChip } from "@/components/app/livePrimitives";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";

const NONE = "__unmapped__";

export default function DataMappingStudioPage() {
  const { roleKey } = useApp();
  const { data: files, refetch: refetchFiles } = useFetch("/live/files");
  const { data: canonical } = useFetch("/live/canonical");
  const { data: savedMappings, refetch: refetchMappings } = useFetch("/live/mappings");
  const { data: templates, refetch: refetchTemplates } = useFetch("/live/mapping-templates");
  const { data: readiness, refetch: refetchReadiness } = useFetch("/live/readiness");

  const tabular = useMemo(() => (files || []).filter((f) => f.kind === "tabular" && f.review_state !== "received"), [files]);
  const [fileId, setFileId] = useState(null);
  const [mode, setMode] = useState("dimensions");
  const [rows, setRows] = useState([]);
  const [loadingSug, setLoadingSug] = useState(false);
  const [applyLog, setApplyLog] = useState(null);

  useEffect(() => {
    if (!fileId && tabular.length > 0) setFileId(tabular[0].id);
  }, [tabular, fileId]);

  const activeFile = tabular.find((f) => f.id === fileId);
  const dimOptions = canonical?.dimensions || [];

  const loadSuggestions = useCallback(async (fid) => {
    setLoadingSug(true);
    setApplyLog(null);
    try {
      const res = await api.post(`/live/mappings/suggest/${fid}`);
      setMode(res.data.mode);
      setRows(res.data.suggestions.map((s) => ({ ...s, target: s.target ?? s.suggested ?? null })));
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Suggestion failed");
      setRows([]);
    } finally {
      setLoadingSug(false);
    }
  }, []);

  useEffect(() => { if (fileId) loadSuggestions(fileId); }, [fileId, loadSuggestions]);

  const setTarget = (source, target) =>
    setRows((rs) => rs.map((r) => (r.source === source ? { ...r, target: target === NONE ? null : target } : r)));

  const saveAndApply = async () => {
    try {
      const res = await api.post("/live/mappings", {
        file_id: fileId, mode, actor_role: roleKey,
        mappings: rows.map((r) => ({ source: r.source, target: r.target })),
      });
      const applied = await api.post(`/live/mappings/${res.data.id}/apply`, { actor_role: roleKey });
      setApplyLog(applied.data.transformation_log);
      toast.success("Mapping saved and applied - dataset marked usable");
      refetchFiles(); refetchMappings(); refetchReadiness();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Save failed");
    }
  };

  const saveTemplate = async () => {
    try {
      await api.post("/live/mapping-templates", {
        name: `${activeFile?.dataset_key || "dataset"} mapping`,
        dataset_key: activeFile?.dataset_key, mode, actor_role: roleKey,
        mappings: rows.map((r) => ({ source: r.source, target: r.target })),
      });
      toast.success("Mapping template saved");
      refetchTemplates();
    } catch (e) { toast.error("Template save failed"); }
  };

  const applyTemplate = (tpl) => {
    setRows((rs) => rs.map((r) => {
      const hit = (tpl.mappings || []).find((m) => m.source === r.source);
      return hit ? { ...r, target: hit.target } : r;
    }));
    toast.success(`Template "${tpl.name}" applied to the working mapping`);
  };

  const mappedCount = rows.filter((r) => r.target).length;

  // Data-quality warnings derived from the file profile (client-side, deterministic)
  const warnings = useMemo(() => {
    const w = [];
    const prof = activeFile?.profile_summary;
    if (!prof) return w;
    if (activeFile.quality_status === "issues" || activeFile.quality_status === "unusable")
      w.push(`File quality is marked '${activeFile.quality_status}' - resolve before analysis.`);
    if (rows.some((r) => !r.target))
      w.push(`${rows.length - mappedCount} source ${mode === "dimensions" ? "dimensions" : "columns"} remain unmapped - they will be excluded from canonical analysis.`);
    if (activeFile.review_state === "partial" || activeFile.review_state === "blocked")
      w.push(`File review state is '${activeFile.review_state}'.`);
    return w;
  }, [activeFile, rows, mappedCount, mode]);

  return (
    <div data-testid="live-mapping-page">
      <PageHeader
        kicker="Live engagement · mapping"
        title="Data Mapping Studio"
        subtitle="Map incoming Brightspeed files to the canonical order-to-install model. Source values are never overwritten - every transformation is logged and traceable."
        right={<LiveBadge />}
      />

      {tabular.length === 0 ? (
        <Panel testId="mapping-empty-panel">
          <EmptyState message="No profiled tabular files yet. Upload a CSV or XLSX in the Data Intake Hub first." />
        </Panel>
      ) : (
        <div className="grid xl:grid-cols-3 gap-4">
          <div className="xl:col-span-2 space-y-4">
            <Panel title="Working file" testId="mapping-file-panel"
              right={activeFile && <ReviewStateChip state={activeFile.review_state} />}>
              <Select value={fileId || ""} onValueChange={setFileId}>
                <SelectTrigger className="h-9 text-xs bg-[--shell-3] border-[--hairline]" data-testid="mapping-file-select">
                  <SelectValue placeholder="Choose a file" />
                </SelectTrigger>
                <SelectContent className="bg-[--shell-2] border-[--hairline]">
                  {tabular.map((f) => (
                    <SelectItem key={f.id} value={f.id} className="text-xs">{f.filename}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
              {activeFile && <div className="mt-2"><SourceTag file={activeFile} /></div>}
            </Panel>

            <Panel
              title={mode === "dimensions" ? "Source dimensions → canonical dimensions" : "Source columns → canonical order fields"}
              testId="mapping-table-panel"
              right={
                <div className="flex items-center gap-2">
                  <span className="text-[10px] font-mono text-[--tx-3]">{mappedCount}/{rows.length} mapped</span>
                  <button onClick={() => loadSuggestions(fileId)} data-testid="mapping-resuggest-button"
                    className="inline-flex items-center gap-1 rounded-lg border border-[--hairline] bg-[--shell-3] px-2 py-1 text-[11px] font-medium text-[--tx-2] hover:text-[--tx-1] transition-colors">
                    <Wand2 size={11} /> Re-detect
                  </button>
                </div>
              }
            >
              {loadingSug && <LoadingState message="Detecting candidate mappings..." />}
              {!loadingSug && rows.length === 0 && <EmptyState message="No mappable structures detected in this file." />}
              {!loadingSug && rows.length > 0 && (
                <div className="space-y-1.5 max-h-[46vh] overflow-y-auto thin-scroll pr-1">
                  {rows.map((r) => (
                    <div key={r.source} className="rounded-lg border border-[--hairline] bg-[--shell-2] px-3 py-2" data-testid={`mapping-row-${r.source.replace(/\s+/g, "-").toLowerCase()}`}>
                      <div className="flex items-center gap-3">
                        <div className="w-[38%] min-w-0">
                          <div className="text-xs font-medium text-[--tx-1] truncate">{r.source}</div>
                          <div className="text-[10px] font-mono text-[--tx-3]">source {mode === "dimensions" ? "dimension" : "column"}</div>
                        </div>
                        <span className="text-[--tx-3] shrink-0">→</span>
                        <div className="flex-1 min-w-0">
                          <Select value={r.target || NONE} onValueChange={(v) => setTarget(r.source, v)}>
                            <SelectTrigger className="h-8 text-xs bg-[--shell-3] border-[--hairline]">
                              <SelectValue />
                            </SelectTrigger>
                            <SelectContent className="bg-[--shell-2] border-[--hairline] max-h-64">
                              <SelectItem value={NONE} className="text-xs text-[--tx-3]">Unmapped (exclude)</SelectItem>
                              {(mode === "dimensions" ? dimOptions : (canonical?.order_fields || [])).map((o) => (
                                <SelectItem key={o.key} value={o.key} className="text-xs">{o.label}</SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                        </div>
                        {r.tier ? (
                          <StatusChip tone={{ high: "validated", medium: "ai", needs_review: "risk" }[r.tier] || "risk"} className="shrink-0 capitalize">
                            {r.tier === "needs_review" ? "Needs review" : r.tier}
                          </StatusChip>
                        ) : (
                          <StatusChip tone={r.confidence >= 0.85 ? "validated" : r.confidence >= 0.5 ? "ai" : "risk"} className="shrink-0">
                            {Math.round(r.confidence * 100)}%
                          </StatusChip>
                        )}
                      </div>
                      {(r.sample_values?.length > 0 || r.transformation || r.missing_rate != null) && (
                        <div className="mt-1.5 flex flex-wrap items-center gap-x-3 gap-y-0.5 text-[10px] font-mono text-[--tx-3]" data-testid={`mapping-detail-${r.source.replace(/\s+/g, "-").toLowerCase()}`}>
                          {r.missing_rate != null && <span>missing {(r.missing_rate * 100).toFixed(0)}%</span>}
                          {r.source_dtype && <span>{String(r.source_dtype).slice(0, 12)}</span>}
                          {r.sample_values?.length > 0 && <span className="truncate max-w-[42%]">e.g. {r.sample_values.slice(0, 3).join(", ")}</span>}
                          {r.target && r.transformation && <span className="text-[--ai]">{r.transformation}</span>}
                          {r.target && r.type_validation && (
                            <span className={r.validation_ok ? "text-[--green]" : "text-[--amber]"}>{r.type_validation}</span>
                          )}
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              )}
              <div className="flex items-center justify-end gap-2 mt-3 pt-3 border-t border-[--divider]">
                <button onClick={saveTemplate} disabled={rows.length === 0} data-testid="mapping-save-template-button"
                  className="inline-flex items-center gap-1.5 rounded-lg border border-[--hairline] bg-[--shell-3] px-3 py-2 text-xs font-semibold text-[--tx-2] hover:text-[--tx-1] disabled:opacity-40 transition-colors">
                  <BookTemplate size={12} /> Save as template
                </button>
                <button onClick={saveAndApply} disabled={rows.length === 0 || mappedCount === 0} data-testid="mapping-apply-button"
                  className="inline-flex items-center gap-1.5 rounded-lg bg-[--six-crimson] hover:bg-[--six-crimson-hover] disabled:opacity-40 active:scale-[0.98] px-3.5 py-2 text-xs font-semibold text-white transition-colors">
                  <Save size={12} /> Save & apply mapping
                </button>
              </div>
            </Panel>

            {applyLog && (
              <Panel title="Transformation log" testId="mapping-transform-log">
                <div className="space-y-1.5">
                  {applyLog.map((l) => (
                    <div key={l} className="flex items-start gap-2 text-xs text-[--tx-2]">
                      <CheckCheck size={12} className="text-[--green] shrink-0 mt-0.5" /> {l}
                    </div>
                  ))}
                </div>
              </Panel>
            )}
          </div>

          <div className="space-y-4">
            {warnings.length > 0 && (
              <Panel title="Data quality warnings" testId="mapping-warnings-panel">
                <div className="space-y-2">
                  {warnings.map((w) => (
                    <div key={w} className="flex items-start gap-2 text-xs text-[--tx-2]">
                      <AlertTriangle size={12} className="text-[--amber] shrink-0 mt-0.5" /> {w}
                    </div>
                  ))}
                </div>
              </Panel>
            )}

            <Panel title="Saved templates" testId="mapping-templates-panel"
              right={<span className="text-[10px] font-mono text-[--tx-3]">{(templates || []).length}</span>}>
              {(templates || []).length === 0 && <EmptyState message="No templates yet. Save one to make re-uploads instant." />}
              <div className="space-y-1.5">
                {(templates || []).map((t) => (
                  <button key={t.id} onClick={() => applyTemplate(t)} data-testid={`template-row-${t.id}`}
                    className="w-full flex items-center justify-between rounded-lg border border-[--hairline] bg-[--shell-2] px-3 py-2 text-left hover:border-[--ai] transition-colors">
                    <div className="min-w-0">
                      <div className="text-xs font-medium text-[--tx-1] truncate">{t.name}</div>
                      <div className="text-[10px] font-mono text-[--tx-3]">{(t.mappings || []).length} mappings · {(t.created_at || "").slice(0, 10)}</div>
                    </div>
                    <StatusChip tone="ai">Apply</StatusChip>
                  </button>
                ))}
              </div>
            </Panel>

            <Panel title="Applied mappings" testId="mapping-history-panel">
              {(savedMappings || []).length === 0 && <EmptyState message="No mappings saved yet." />}
              <div className="space-y-1.5">
                {(savedMappings || []).slice(0, 8).map((m) => (
                  <div key={m.id} className="rounded-lg border border-[--hairline] bg-[--shell-2] px-3 py-2">
                    <div className="flex items-center justify-between gap-2">
                      <span className="text-xs font-medium text-[--tx-1] truncate">{m.filename}</span>
                      <StatusChip tone={m.applied ? "validated" : "neutral"}>{m.applied ? "Applied" : "Saved"}</StatusChip>
                    </div>
                    <div className="text-[10px] font-mono text-[--tx-3] mt-0.5">{(m.mappings || []).length} mappings · {(m.created_at || "").slice(0, 16).replace("T", " ")}</div>
                  </div>
                ))}
              </div>
            </Panel>

            <Panel title="Canonical order-to-install model" testId="mapping-canonical-panel"
              right={<Layers size={12} className="text-[--tx-3]" />}>
              <p className="text-[11px] text-[--tx-3] leading-relaxed mb-2">
                Row-level target model. Awaiting an order-level extract - required fields marked below.
              </p>
              <div className="flex flex-wrap gap-1">
                {(canonical?.order_fields || []).map((f) => (
                  <span key={f.key} className={`rounded px-1.5 py-0.5 text-[10px] font-mono border border-white/10 ${f.required ? "bg-[--amber-soft] text-[--amber]" : "bg-white/5 text-[--tx-3]"}`}>
                    {f.key}
                  </span>
                ))}
              </div>
            </Panel>
          </div>
        </div>
      )}

      {readiness?.tables?.length > 0 && (
        <div className="mt-4">
          <Panel title="Data readiness" testId="readiness-panel"
            right={<span className="text-[10px] font-mono text-[--tx-3]">{readiness.tables.length} tables · computed {(readiness.as_of || "").slice(11, 16)} UTC</span>}>
            <div className="grid md:grid-cols-2 xl:grid-cols-3 gap-3">
              {readiness.tables.map((t) => (
                <div key={`${t.file_id}-${t.table}`} className="rounded-xl border border-[--hairline] bg-[--shell-2] p-3" data-testid={`readiness-card-${t.file_id}`}>
                  <div className="flex items-center gap-2 mb-1.5">
                    <span className="text-xs font-semibold text-[--tx-1] truncate">{t.filename}</span>
                    <StatusChip tone={t.kind === "order_level" ? "validated" : t.kind === "segment_metrics" ? "ai" : "neutral"} className="ml-auto shrink-0">
                      {t.kind === "order_level" ? "Order level" : t.kind === "segment_metrics" ? "Segment metrics" : "Profiled"}
                    </StatusChip>
                  </div>
                  <div className="grid grid-cols-2 gap-x-3 gap-y-0.5 text-[10px] font-mono text-[--tx-3]">
                    <span>rows <span className="text-[--tx-1]">{(t.rows_loaded ?? 0).toLocaleString()}</span></span>
                    {t.coverage?.[0] && <span>coverage <span className="text-[--tx-1]">{String(t.coverage[0]).slice(0, 10)} → {String(t.coverage[1]).slice(0, 10)}</span></span>}
                    {t.unique_order_ids != null && <span>unique IDs <span className="text-[--tx-1]">{t.unique_order_ids.toLocaleString()}</span></span>}
                    {t.duplicate_rate != null && <span>dup rate <span className={t.duplicate_rate > 0 ? "text-[--amber]" : "text-[--green]"}>{(t.duplicate_rate * 100).toFixed(1)}%</span></span>}
                    {t.missing_order_ids != null && <span>missing IDs <span className={t.missing_order_ids > 0 ? "text-[--amber]" : "text-[--green]"}>{t.missing_order_ids}</span></span>}
                    {t.missing_reason_rate != null && <span>missing reasons <span className={t.missing_reason_rate > 0.2 ? "text-[--amber]" : "text-[--tx-1]"}>{(t.missing_reason_rate * 100).toFixed(0)}%</span></span>}
                    {t.missing_channel_rate != null && <span>missing channel <span className="text-[--tx-1]">{(t.missing_channel_rate * 100).toFixed(0)}%</span></span>}
                    {t.missing_market_rate != null && <span>missing market <span className="text-[--tx-1]">{(t.missing_market_rate * 100).toFixed(0)}%</span></span>}
                    {t.invalid_dates != null && <span>invalid dates <span className={t.invalid_dates > 0 ? "text-[--amber]" : "text-[--green]"}>{t.invalid_dates}</span></span>}
                    {t.months_covered && <span>months <span className="text-[--tx-1]">{t.months_covered.length}</span></span>}
                    {t.canonical_share != null && <span>canonical <span className="text-[--tx-1]">{(t.canonical_share * 100).toFixed(0)}%</span></span>}
                  </div>
                  {t.unmapped_required_fields?.length > 0 && (
                    <div className="mt-1.5 text-[10px] font-mono text-[--amber]">unmapped required: {t.unmapped_required_fields.join(", ")}</div>
                  )}
                  {t.sensitive_columns?.length > 0 && (
                    <div className="mt-1 text-[10px] font-mono text-[--six-crimson]">sensitive: {t.sensitive_columns.join(", ")}</div>
                  )}
                  {t.cancels_only && <div className="mt-1 text-[10px] font-mono text-[--amber]">cancels-only extract - no population denominator</div>}
                </div>
              ))}
            </div>
            {readiness.join_readiness?.length > 0 && (
              <div className="mt-3 pt-3 border-t border-[--divider]" data-testid="join-readiness">
                <div className="text-[10px] font-mono uppercase tracking-wider text-[--tx-3] mb-1.5">Join readiness</div>
                <div className="space-y-1">
                  {readiness.join_readiness.slice(0, 8).map((j, i) => (
                    <div key={`${j.table_a}-${j.table_b}-${i}`} className="flex items-center gap-2 text-[10px] font-mono">
                      <span className={j.joinable ? "text-[--green]" : "text-[--amber]"}>{j.joinable ? "●" : "○"}</span>
                      <span className="text-[--tx-2] truncate">{j.table_a} × {j.table_b}</span>
                      <span className="text-[--tx-3] truncate hidden md:inline">- {j.note}</span>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </Panel>
        </div>
      )}
    </div>
  );
}
